import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { SnRecordingsAPI } from '@lib/api/services';
import { SnSmartBadgeService } from '@lib/ui/badge';

import { SnLiveIncident } from '../models';

export class SnIncidentsTracking {
  private readonly _incidentStarted$ = new Subject<SnLiveIncident>();
  private readonly _incidentStopped$ = new Subject<SnLiveIncident>();

  private readonly _incidentMapping = new Map<string, SnLiveIncident>();
  private _incidentList: SnLiveIncident[] = [];

  private readonly _playableIncidentList = new Set<string>();

  constructor(
    private readonly _recordingsAPI: SnRecordingsAPI,
    private readonly _smartBadge: SnSmartBadgeService,
  ) {}

  get incidentList(): SnLiveIncident[] {
    return this._incidentList;
  }

  get incidentCount(): number {
    return this._incidentMapping.size;
  }

  get incidentStarted$(): Observable<SnLiveIncident> {
    return this._incidentStarted$.asObservable();
  }

  get incidentStopped$(): Observable<SnLiveIncident> {
    return this._incidentStopped$.asObservable();
  }

  public addIncident(incident: SnLiveIncident, emitEvent: boolean = true): void {
    this._incidentMapping.set(incident.id, incident);
    this._updateIncidentList();

    if (emitEvent) {
      this._incidentStarted$.next(incident);
    }
  }

  public removeIncident(id: string, emitEvent: boolean = true): void {
    this.getIncident(id).subscribe((incident) => {
      this._incidentMapping.delete(id);
      this._updateIncidentList();

      this.stopIncident(incident);

      if (emitEvent) {
        this._incidentStopped$.next(incident);
      }
    });
  }

  public removeCitizenIncident(incidentId: string, emitEvent: boolean = true): void {
    const incident = this._incidentMapping.get(incidentId);
    this._incidentMapping.delete(incidentId);
    this._updateIncidentList();

    if (emitEvent) {
      this._incidentStopped$.next(incident);
    }
  }

  public hasPlayableIncident(): boolean {
    return !!this._playableIncidentList.size;
  }

  public playIncident(incident: SnLiveIncident): void {
    this._playableIncidentList.add(incident.id);
  }

  public stopIncident(incident: SnLiveIncident): void {
    this._playableIncidentList.delete(incident.id);
  }

  public getIncident(id: string): Observable<SnLiveIncident> {
    return new Observable<SnLiveIncident>((observer) => {
      const localIncident = this._incidentMapping.get(id);

      if (localIncident) {
        observer.next(localIncident);
        observer.complete();
      } else {
        this._recordingsAPI
          .getRawIncident(id)
          .pipe(
            map((response: any) => new SnLiveIncident(response)),
            filter((incident) => !!incident.liveUrls),
          )
          .subscribe({
            next: (incident) => {
              observer.next(incident);
              observer.complete();
            },
          });
      }
    });
  }

  public clear(): void {
    this._incidentMapping.clear();
    this._updateIncidentList();
  }

  public destroy(): void {
    this.clear();

    this._incidentStarted$.complete();
    this._incidentStopped$.complete();
  }

  private _updateIncidentList(): void {
    this._incidentList = Array.from(this._incidentMapping.values());
    this._smartBadge.setBadge('live-tracking__incident-count', this._incidentList.length);
  }
}
